import React from "react";
import { Redirect, Route } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import Switch from "react-router-transition-switch";
import Loadable from "react-loadable";
import { connect } from "react-redux";

import Layout from "layouts";
import Loader from "components/layout/Loader";
import NotFoundPage from "pages/system/404";

const loadable = loader =>
  Loadable({
    loader,
    delay: false,
    loading: () => <Loader />
  });

const routes = [
  {
    path: "/home",
    Component: loadable(() => import("pages/home")),
    exact: true
  },
  {
    path: "/facility",
    Component: loadable(() => import("pages/facility")),
    exact: true
  },
  {
    path: "/multiple-facility-lots",
    Component: loadable(() => import("pages/multi-facility-lots")),
    exact: true
  },
  {
    path: "/facility/return-limit",
    Component: loadable(() => import("pages/facility/facilityReturnLimit")),
    exact: true
  },
  {
    path: "/lot-discount",
    Component: loadable(() => import("pages/lot-discount")),
    exact: true
  },
  {
    path: "/lot-discount/add",
    Component: loadable(() => import("pages/lot-discount/lotDiscountForm")),
    exact: true
  },

  {
    path: "/lot-simulation",
    Component: loadable(() => import("pages/lot-simulation")),
    exact: true
  },
  {
    path: "/lot-simulation/add",
    Component: loadable(() => import("pages/lot-simulation/lotSimulationForm")),
    exact: true
  },

  {
    path: "/user",
    Component: loadable(() => import("pages/user")),
    exact: true
  },
  {
    path: "/user/add",
    Component: loadable(() => import("pages/user/userForm")),
    exact: true
  },
  {
    path: "/user/device-limit-upload",
    Component: loadable(() => import("pages/user/DeviceLimitUpload")),
    exact: true
  },
  {
    path: "/user/role/:id",
    Component: loadable(() => import("pages/user/userRoleList")),
    exact: true
  },
  {
    path: "/user/facility/:id",
    Component: loadable(() => import("pages/user/userFacilityList")),
    exact: true
  },

  {
    path: "/facility/lot/:facility",
    Component: loadable(() => import("pages/lot")),
    exact: true
  },
  {
    path: "/facility/lot/upload/:facility",
    Component: loadable(() => import("pages/lot/uploadLot")),
    exact: true
  },
  {
    path: "/multiple-facility-lots/upload",
    Component: loadable(() => import("pages/multi-facility-lots/uploadLot")),
    exact: true
  },
  {
    path: "/facility/lot/create/:facility",
    Component: loadable(() => import("pages/lot/createLot")),
    exact: true
  },
  {
    path: "/facility/lot/simulation/create/:facility",
    Component: loadable(() => import("pages/lot-simulation-criteria")),
    exact: true
  },
  {
    path: "/facility/lot/edit/:facility/:lot",
    Component: loadable(() => import("pages/lot/createLot")),
    exact: true
  },
  {
    path: "/facility/lot/detail/:lot",
    Component: loadable(() => import("pages/lot/lotSimulation")),
    exact: true
  },
  {
    path: "/facility/lot/view/:facility/:lot",
    Component: loadable(() => import("pages/lot/viewLot")),
    exact: true
  },
  {
    path: "/facility/lot/feed-price/:facility/:lot",
    Component: loadable(() => import("pages/lot/feedChannelLotPrice")),
    exact: true
  },
  {
    path: "/facility/:facility/lot/user/:lot",
    Component: loadable(() => import("pages/lot/assignSalesUser")),
    exact: true
  },
  {
    path: "/facility/:facility/lot/vendor/:lot",
    Component: loadable(() => import("pages/lot/assignVendor")),
    exact: true
  },
  {
    path: "/system/login",
    Component: loadable(() => import("pages/system/login")),
    exact: true
  },
  {
    path: "/403",
    Component: loadable(() => import("pages/system/403")),
    exact: true
  },

  {
    path: "/profile",
    Component: loadable(() => import("pages/profile")),
    exact: true
  },

  {
    path: "/analytics/inventory/brand",
    Component: loadable(() => import("pages/analytics/brandInventory")),
    exact: true
  },
  {
    path:
      "/analytics/inventory/brand/model/:brandId/:brandName/:selectedFacility",
    Component: loadable(() => import("pages/analytics/modelInventory")),
    exact: true
  },
  {
    path:
      "/analytics/inventory/brand/model/grade/:brandId/:brandName/:modelId/:modelName/:selectedFacility",
    Component: loadable(() => import("pages/analytics/gradeInventory")),
    exact: true
  },
  {
    path: "/analytics/lot-summary",
    Component: loadable(() => import("pages/analytics/lotSummary")),
    exact: true
  },
  {
    path: "/analytics/brand-ageing",
    Component: loadable(() =>
      import("pages/analytics/brandInventoryAgeReport")
    ),
    exact: true
  },
  {
    path: "/vendor/lot/:code/requote/:lot",
    Component: loadable(() => import("pages/vendor/deviceRequoteReport")),
    exact: true
  },
  {
    path: "/analytics/product-price",
    Component: loadable(() => import("pages/analytics/productPrice")),
    exact: true
  },
  {
    path: "/vendor/details/:code",
    Component: loadable(() => import("pages/vendor/vendorView")),
    exact: true
  },
  {
    path: "/vendor",
    Component: loadable(() => import("pages/vendor")),
    exact: true
  },
  {
    path: "/vendor-search",
    Component: loadable(() => import("pages/vendor/vendorSearch")),
    exact: true
  },
  {
    path: "/vendor/facility/:code",
    Component: loadable(() => import("pages/vendor/facility")),
    exact: true
  },
  {
    path: "/vendor/docs/:code",
    Component: loadable(() => import("pages/vendor/addVendorDocs")),
    exact: true
  },
  {
    path: "/vendor/updateDocs/:code/:updateType",
    Component: loadable(() => import("pages/vendor/updateVendorDocs")),
    exact: true
  },
  {
    path: "/vendor/address/:code",
    Component: loadable(() => import("pages/vendor/addVendorAddress")),
    exact: true
  },
  {
    path: "/vendor/updateAddress/:code/:updateType",
    Component: loadable(() => import("pages/vendor/updateVendorAddress")),
    exact: true
  },
  {
    path: "/vendor/collect-payment/:code",
    Component: loadable(() => import("pages/vendor/collectPayment")),
    exact: true
  },
  {
    path: "/vendor/refund-payment/:code",
    Component: loadable(() => import("pages/vendor/refundPayment")),
    exact: true
  },
  {
    path: "/vendor/ledger/:code",
    Component: loadable(() => import("pages/vendor/ledger")),
    exact: true
  },
  {
    path: "/vendor/coin-transaction/:code",
    Component: loadable(() => import("pages/vendor/coinTransaction")),
    exact: true
  },
  {
    path: "/vendor/lot/:code",
    Component: loadable(() => import("pages/vendor/lot")),
    exact: true
  },
  {
    path: "/vendor/add",
    Component: loadable(() => import("pages/vendor/vendorForm")),
    exact: true
  },
  {
    path: "/vendor/lot/:code/merge-lot",
    Component: loadable(() => import("pages/vendor/mergeLot")),
    exact: true
  },

  {
    path: "/vendor/lot/:code/view/:lot",
    Component: loadable(() => import("pages/vendor/lotView")),
    exact: true
  },
  {
    path: "/vendor/lot/:code/return/:lot",
    Component: loadable(() => import("pages/vendor/returnDevice")),
    exact: true
  },
  {
    path: "/vendor/lot-invoice",
    Component: loadable(() => import("pages/vendor/lotInvoice")),
    exact: true
  },
  {
    path: "/download",
    Component: loadable(() => import("pages/downloads")),
    exact: true
  },
  {
    path: "/refund",
    Component: loadable(() => import("pages/refund")),
    exact: true
  },
  {
    path: "/additional-lot-discount",
    Component: loadable(() => import("pages/additional-lot-discount/index")),
    exact: true
  }
];

const mapStateToProps = ({ settings }) => ({ settings });

@connect(mapStateToProps)
class Router extends React.Component {
  render() {
    const {
      history,
      settings: { routerAnimation }
    } = this.props;
    return (
      <ConnectedRouter history={history}>
        <Layout>
          <Switch
            render={props => {
              const {
                children,
                location: { pathname }
              } = props;
              return (
                <SwitchTransition>
                  <CSSTransition
                    key={pathname}
                    classNames={routerAnimation}
                    timeout={routerAnimation === "none" ? 0 : 300}
                  >
                    {children}
                  </CSSTransition>
                </SwitchTransition>
              );
            }}
          >
            <Route exact path="/" render={() => <Redirect to="/home" />} />
            {routes.map(({ path, Component, exact }) => (
              <Route path={path} key={path} exact={exact}>
                <Component />
              </Route>
            ))}
            <Route component={NotFoundPage} />
          </Switch>
        </Layout>
      </ConnectedRouter>
    );
  }
}

export default Router;
